import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotificationById,
  clearAllNotificationsFromDB,
  getUnreadNotificationCount,
} from "../api/notifications.api";
import { toast } from "react-toastify";

// Define query keys for proper cache invalidation
const NOTIFICATION_QUERY_KEYS = {
  all: ["notifications"] as const,
  lists: () => [...NOTIFICATION_QUERY_KEYS.all, "list"] as const,
  list: (params?: {
    page?: number;
    limit?: number;
    category?: string;
    priority?: string;
    isRead?: boolean | string;
    search?: string;
  }) => [...NOTIFICATION_QUERY_KEYS.lists(), params] as const,
  counts: () => [...NOTIFICATION_QUERY_KEYS.all, "count"] as const,
  unreadCount: () => [...NOTIFICATION_QUERY_KEYS.counts(), "unread"] as const,
};

interface FetchNotificationsParams {
  page?: number;
  limit?: number;
  category?: string;
  priority?: string;
  isRead?: boolean | string;
  search?: string;
  pollingInterval?: number; // Default polling interval in ms
  enabled?: boolean; // Enable/disable auto-polling
}

/**
 * Hook for fetching notifications with TanStack Query
 * Replaces the custom useNotificationPolling hook with proper polling via refetchInterval
 *
 * Benefits over custom polling:
 * - Built-in caching and deduplication
 * - Automatic window focus refetching
 * - Built-in stale time management
 * - Error recovery with exponential backoff retry
 * - Better memory management (no circular ref leaks)
 * - Server state synchronized via proper query keys
 */
export const useQueriedNotifications = (params?: FetchNotificationsParams) => {
  const {
    pollingInterval, // Ignored now
    enabled = true,
    ...queryParams
  } = params || {};

  return useQuery({
    queryKey: NOTIFICATION_QUERY_KEYS.list(queryParams),
    queryFn: () => fetchNotifications(queryParams),

    // Polling configuration - entirely disabled as per user request to only fetch when required
    refetchInterval: false,

    // Auto-refetch when window regains focus - disabled to prevent random API calls
    refetchOnWindowFocus: false,

    // Fresh fetch on component mount
    refetchOnMount: true,

    // Keep data fresh basically forever until manual refetch
    staleTime: Infinity,

    // Keep data in cache for 5 minutes even after unmount
    gcTime: 5 * 60 * 1000,

    // Retry logic: do NOT retry on 401/403 auth errors (no point — the token
    // is invalid and retrying will just generate more unnecessary network calls).
    // Only retry on genuine network failures or 5xx server errors.
    retry: (failureCount, error: any) => {
      const status = error?.response?.status;
      if (status === 401 || status === 403) return false; // auth error — don't retry
      return failureCount < 2; // retry up to 2 times for other errors
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

    enabled,
  });
};

/**
 * Hook for fetching unread notification count
 * Used to display badge count with auto-retry on failure
 */
export const useUnreadNotificationCount = (
  enabled: boolean = true,
  pollingInterval?: number, // Ignored now
) => {
  return useQuery({
    queryKey: NOTIFICATION_QUERY_KEYS.unreadCount(),
    queryFn: getUnreadNotificationCount,

    // Poll for updates - Disabled per user request
    refetchInterval: false,

    // Prevent fetching automatically on window focus
    refetchOnWindowFocus: false,
    refetchOnMount: true,

    // Keep data fresh until manual refetch to avoid background calls
    staleTime: Infinity,
    gcTime: 5 * 60 * 1000,

    // Retry logic: stop on 401/403, retry up to 2x for other errors
    retry: (failureCount, error: any) => {
      const status = error?.response?.status;
      if (status === 401 || status === 403) return false;
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

    enabled,
  });
};

/**
 * Hook for marking a single notification as read
 */
export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string | number) => markNotificationAsRead(id),

    onSuccess: () => {
      // Invalidate all notification queries to refetch fresh data
      queryClient.invalidateQueries({
        queryKey: NOTIFICATION_QUERY_KEYS.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: NOTIFICATION_QUERY_KEYS.unreadCount(),
      });
    },

    onError: (error) => {
      console.error("Failed to mark notification as read:", error);
      toast.error("Failed to mark notification as read", {
        position: "top-center",
        autoClose: 3000,
      });
    },

    // Retry on network failures
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
};

/**
 * Hook for marking all notifications as read
 */
export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => markAllNotificationsAsRead(),

    onSuccess: () => {
      // Invalidate all notification queries
      queryClient.invalidateQueries({
        queryKey: NOTIFICATION_QUERY_KEYS.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: NOTIFICATION_QUERY_KEYS.unreadCount(),
      });
      toast.success("All notifications marked as read", {
        position: "top-center",
        autoClose: 2000,
      });
    },

    onError: (error) => {
      console.error("Failed to mark all notifications as read:", error);
      toast.error("Failed to mark all notifications as read", {
        position: "top-center",
        autoClose: 3000,
      });
    },

    // Retry on network failures
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
};

/**
 * Hook for deleting a notification
 */
export const useDeleteNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string | number) => deleteNotificationById(id),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: NOTIFICATION_QUERY_KEYS.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: NOTIFICATION_QUERY_KEYS.unreadCount(),
      });
      toast.success("Notification deleted", {
        position: "top-center",
        autoClose: 2000,
      });
    },

    onError: (error) => {
      console.error("Failed to delete notification:", error);
      toast.error("Failed to delete notification", {
        position: "top-center",
        autoClose: 3000,
      });
    },

    // Retry on network failures
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
};

/**
 * Hook for clearing all notifications
 */
export const useClearAllNotifications = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => clearAllNotificationsFromDB(),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: NOTIFICATION_QUERY_KEYS.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: NOTIFICATION_QUERY_KEYS.unreadCount(),
      });
      toast.success("All notifications cleared", {
        position: "top-center",
        autoClose: 2000,
      });
    },

    onError: (error) => {
      console.error("Failed to clear all notifications:", error);
      toast.error("Failed to clear all notifications", {
        position: "top-center",
        autoClose: 3000,
      });
    },

    // Retry on network failures
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
};

/**
 * Helper function to invalidate all notification queries
 * Call this when user logs out to clean up cached data
 */
export const invalidateAllNotificationQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  queryClient.invalidateQueries({
    queryKey: NOTIFICATION_QUERY_KEYS.all,
  });
};

// Export query keys for advanced cache management
export { NOTIFICATION_QUERY_KEYS };
